/* ============================================================
   Verhalten der Shop-Landingpage (Experiment auf agent-growth).

   Drei Custom Elements, bewusst ohne Import aus theme.js: die Modulnamen
   "theme"/"vendor" der Importmap stehen nur Modul-Skripten offen, diese
   Datei wird wie custom.js klassisch mit defer geladen.

   <dreissigacker-tabs>        Umschalter fuer die Produkt-Rail.
   <dreissigacker-rail-pager>  Seitenweises Blaettern einer Produktreihe.
   <dreissigacker-cart-sync>   Haelt einen Abschnitt am Warenkorb-Stand.

   Geladen von den shop-*-Sections.
   ============================================================ */

(() => {
  'use strict';

  /* Jede Section, die diese Datei braucht, gibt ihr eigenes <script defer>
     aus; auf /collections/shop stehen fuenf davon. Klassische Skripte laufen
     pro Tag, nur customElements.define war bisher abgesichert. Ohne diese
     Sperre lagen fuenf MutationObserver auf dem ganzen Dokument und fuenf
     resize-Listener uebereinander. */
  if (window.dreissigackerShopLanding) return;
  window.dreissigackerShopLanding = true;

  const root = window.Shopify?.routes?.root || '/';

  /* Hoehe der fixierten Warenkorbleiste als --shop-stickybar-h veroeffentlichen.
     Bewusst ausserhalb der Custom Elements: die Leiste entsteht und verschwindet
     mit dem Warenkorb, und der Platz, den die Seite unten freihalten muss, haengt
     nur an ihrer gemessenen Hoehe -- nicht daran, welches Element sie gerade
     gerendert hat. Auf schmalen Viewports bricht sie auf zwei bis drei Zeilen um,
     der CSS-Vorgabewert von 84px reicht dort nicht. */
  let barFrame = 0;
  let barHeight = null;

  const measureBar = () => {
    const bar = document.querySelector('.shop-stickybar');
    const height = bar ? Math.round(bar.getBoundingClientRect().height) : 0;
    if (height === barHeight) return;
    barHeight = height;
    if (height > 0) document.documentElement.style.setProperty('--shop-stickybar-h', `${height}px`);
    else document.documentElement.style.removeProperty('--shop-stickybar-h');
  };

  // immediate=true misst ohne requestAnimationFrame. Noetig fuer die erste
  // Messung: in einem Hintergrund-Tab drosselt der Browser rAF und den
  // ResizeObserver, die Leiste bliebe dann bis zum ersten Fokus unvermessen.
  const publishBarHeight = (immediate) => {
    if (immediate) {
      measureBar();
      return;
    }
    if (barFrame) return;
    barFrame = requestAnimationFrame(() => {
      barFrame = 0;
      measureBar();
    });
  };

  /* Nur, wenn die Leiste ueberhaupt auf der Seite ist (Section-Wrapper von
     sections/shop-sticky-cart.liquid, rendert auch bei leerem Warenkorb): sonst
     liefe der Beobachter auf jeder DOM-Aenderung fuer nichts -- und
     <dreissigacker-cart-sync> steht ueber die Kopfzeile auf jeder Shop-Seite. */
  let barWatched = false;

  const watchBar = () => {
    if (barWatched || typeof ResizeObserver !== 'function' || !document.querySelector('.shopify-section--shop-sticky-cart')) return;
    barWatched = true;
    // Ohne Argument aufrufen: ResizeObserver und Events uebergeben sonst ein
    // truthy erstes Argument, und der rAF-gedrosselte Pfad kaeme nie dran.
    const barObserver = new ResizeObserver(() => publishBarHeight());
    let observed = null;
    const rebind = () => {
      const bar = document.querySelector('.shop-stickybar');
      if (bar === observed) return;
      if (observed) barObserver.unobserve(observed);
      if (bar) barObserver.observe(bar);
      observed = bar;
      publishBarHeight(true);
    };
    // Die Leiste wird bei jeder Warenkorbaenderung neu erzeugt, deshalb den
    // ganzen Body beobachten statt einer Referenz zu vertrauen.
    new MutationObserver(rebind).observe(document.documentElement, { childList: true, subtree: true });
    rebind();
  };

  watchBar();
  // Im Theme-Editor kann die Leiste erst nachtraeglich als Section dazukommen.
  document.addEventListener('shopify:section:load', watchBar);

  window.addEventListener('resize', () => publishBarHeight());
  window.addEventListener('pageshow', () => publishBarHeight());

  /* Kacheln einer Reihe vorladen. Mit loading="lazy" laedt der Browser Kacheln
     rechts ausserhalb des Scrollausschnitts und in versteckten Reitern erst,
     wenn sie im Bild sind: nach Blaettern oder Reiterwechsel standen sie leer. */
  const preloadImages = (scope) => {
    scope?.querySelectorAll('img[loading="lazy"]').forEach((img) => img.removeAttribute('loading'));
  };

  const railObserver = typeof IntersectionObserver === 'function'
    ? new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        railObserver.unobserve(entry.target);
        preloadImages(entry.target);
      });
    }, { rootMargin: '100% 0px' })
    : null;

  const watchRails = () => {
    document.querySelectorAll('.shop-rail__loop').forEach((list) => {
      if (railObserver) railObserver.observe(list);
      else preloadImages(list);
    });
  };

  watchRails();
  document.addEventListener('shopify:section:load', watchRails);

  // Der Editor rendert die Section bei jeder Einstellung neu; ohne Merker sprang die Auswahl auf die erste Gruppe.
  const selectedPanels = new Map();

  class ShopTabs extends HTMLElement {
    disconnectedCallback() {
      this.abortController?.abort();
    }

    connectedCallback() {
      this.abortController?.abort();
      this.abortController = new AbortController();
      const { signal } = this.abortController;

      this.tabs = [...this.querySelectorAll('[role="tab"]')];
      // Bei nur einer Gruppe rendert die Section kein role="tabpanel", deshalb
      // zusaetzlich ueber die Klasse suchen.
      this.panels = [...this.querySelectorAll('.shop-rail__panel')];

      // Im Theme-Editor die Gruppe des angewaehlten Blocks zeigen, sonst bliebe sie hinter ihrem Reiter versteckt.
      if (window.Shopify?.designMode) {
        this.addEventListener('shopify:block:select', (event) => {
          const panel = event.target.closest?.('.shop-rail__panel');
          if (panel && this.tabs.length > 1 && this.panels.includes(panel)) this.select(panel.id);
        }, { signal });
      }

      if (this.tabs.length < 2) {
        // Eine einzige Gruppe braucht keine Leiste, ihr Panel bleibt sichtbar.
        this.classList.add('shop-rail--no-js');
        this.panels.forEach((panel) => {
          panel.hidden = false;
        });
        return;
      }

      this.classList.remove('shop-rail--no-js');
      // Der Server liefert alle Panels ausser dem Startreiter mit hidden aus, damit
      // die Seite beim spaeten Upgrade nicht zusammenspringt. Verlassen duerfen
      // wir uns darauf nicht, select() setzt den Zustand vollstaendig.
      const saved = selectedPanels.get(this.sectionKey);
      const initial = this.tabs.find((tab) => tab.getAttribute('aria-selected') === 'true') || this.tabs[0];
      this.select(this.panels.some((panel) => panel.id === saved) ? saved : initial.getAttribute('aria-controls'), false);

      this.tabs.forEach((tab) => {
        // Vor dem Klick laden, damit die Gruppe beim Umschalten schon Bilder hat.
        const preloadPanel = () => preloadImages(document.getElementById(tab.getAttribute('aria-controls')));
        tab.addEventListener('pointerenter', preloadPanel, { signal });
        tab.addEventListener('focus', preloadPanel, { signal });
        tab.addEventListener('click', () => this.select(tab.getAttribute('aria-controls'), true), { signal });
        tab.addEventListener('keydown', (event) => {
          const index = this.tabs.indexOf(tab);
          let target = -1;

          if (event.key === 'ArrowRight') target = (index + 1) % this.tabs.length;
          else if (event.key === 'ArrowLeft') target = (index - 1 + this.tabs.length) % this.tabs.length;
          else if (event.key === 'Home') target = 0;
          else if (event.key === 'End') target = this.tabs.length - 1;

          if (target < 0) return;
          event.preventDefault();
          const next = this.tabs[target];
          next.focus();
          this.select(next.getAttribute('aria-controls'), true);
        }, { signal });
      });
    }

    get sectionKey() {
      return this.closest('.shopify-section')?.id || '';
    }

    revealTab(tab, animate = true) {
      const list = tab?.closest('.shop-rail__tabs');
      if (!list || list.scrollWidth <= list.clientWidth) return;

      const style = getComputedStyle(list);
      const paddingStart = parseFloat(style.paddingInlineStart) || 0;
      const paddingEnd = parseFloat(style.paddingInlineEnd) || 0;
      const tabStart = tab.offsetLeft;
      const tabEnd = tabStart + tab.offsetWidth;
      const viewStart = list.scrollLeft + paddingStart;
      const viewEnd = list.scrollLeft + list.clientWidth - paddingEnd;
      const edge = 8;

      let left = null;
      if (tabStart < viewStart + edge) left = tabStart - paddingStart - edge;
      else if (tabEnd > viewEnd - edge) left = tabEnd - list.clientWidth + paddingEnd + edge;

      if (left === null) return;

      const reduceMotion = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
      list.scrollTo({
        left: Math.max(0, left),
        behavior: animate && !reduceMotion ? 'smooth' : 'auto'
      });
    }

    select(panelId, animate = true) {
      selectedPanels.set(this.sectionKey, panelId);
      let activeTab = null;
      this.tabs.forEach((tab) => {
        const active = tab.getAttribute('aria-controls') === panelId;
        tab.setAttribute('aria-selected', active ? 'true' : 'false');
        tab.setAttribute('tabindex', active ? '0' : '-1');
        if (active) activeTab = tab;
      });
      this.panels.forEach((panel) => {
        panel.hidden = panel.id !== panelId;
      });
      if (animate) preloadImages(document.getElementById(panelId));
      this.revealTab(activeTab, animate);
    }
  }

  // Eigener Namensraum: "shop-cart-sync" war auf diesem Storefront bereits von
  // einem fremden Skript belegt. Der Guard hat die eigene Registrierung dann
  // still uebersprungen, und die fremde Klasse hing einen Shadow Root an --
  // damit war der komplette Light-DOM-Inhalt (Sticky-Leiste, Versandmodul)
  // im DOM vorhanden, aber unsichtbar.
  class ShopCartSync extends HTMLElement {
    connectedCallback() {
      this.sectionId = this.getAttribute('section-id');
      this.pending = false;

      // Die Hoehe der Leiste gehoert hierher und nicht in ein eigenes Element:
      // <dreissigacker-cart-sync> ersetzt bei jedem cart:change seinen kompletten Inhalt,
      // kennt also als einziges den Moment, in dem die Leiste neu entsteht,
      // verschwindet oder ihre Zeilenzahl aendert.

      // Das Theme sammelt vor jedem cart/add.js ein, welche Sections der Server
      // mitrendern soll (assets/theme.js, <cart-drawer>). Wer sich hier
      // eintraegt, bekommt sein HTML frei Haus und spart den zweiten Request.
      this.onPrepare = (event) => {
        if (this.sectionId) event.detail?.sections?.add(this.sectionId);
      };

      this.onChange = (event) => {
        const html = this.sectionId ? event.detail?.cart?.sections?.[this.sectionId] : null;
        if (html) this.swap(html);
        else this.reload();
      };

      this.onRefresh = () => this.reload();

      this.onPageShow = (event) => {
        if (event.persisted) this.reload();
      };

      document.documentElement.addEventListener('cart:prepare-bundled-sections', this.onPrepare);
      document.documentElement.addEventListener('cart:change', this.onChange);
      document.documentElement.addEventListener('cart:refresh', this.onRefresh);
      window.addEventListener('pageshow', this.onPageShow);
      this.bindBar();
    }

    disconnectedCallback() {
      document.documentElement.removeEventListener('cart:prepare-bundled-sections', this.onPrepare);
      document.documentElement.removeEventListener('cart:change', this.onChange);
      document.documentElement.removeEventListener('cart:refresh', this.onRefresh);
      window.removeEventListener('pageshow', this.onPageShow);
      publishBarHeight();
    }

    bindBar() {
      publishBarHeight(true);
    }

    async reload() {
      // Im Editor lieferte der Abruf den gespeicherten Stand und ueberschriebe die ungespeicherte Vorschau.
      if (window.Shopify?.designMode) return;
      if (!this.sectionId || this.pending) return;
      this.pending = true;

      try {
        const response = await fetch(`${root}?section_id=${encodeURIComponent(this.sectionId)}`, {
          credentials: 'same-origin'
        });
        if (response.ok) this.swap(await response.text());
      } catch {
        // Ein fehlgeschlagener Refresh darf die Seite nicht blockieren, der
        // naechste Warenkorb-Event versucht es erneut.
      } finally {
        this.pending = false;
      }
    }

    swap(html) {
      // Mitgelieferte Section-HTML ist ebenfalls der gespeicherte Stand, siehe reload().
      if (window.Shopify?.designMode) return;
      const parsed = new DOMParser().parseFromString(html, 'text/html');
      const next = parsed.querySelector(`dreissigacker-cart-sync[section-id="${this.sectionId}"]`);
      if (next) {
        this.innerHTML = next.innerHTML;
        this.bindBar();
      }
    }
  }

  /* Seitenweises Blaettern einer Produktreihe (sections/shop-product-rail.liquid).
     Eine Seite sind so viele Kacheln, wie nebeneinander passen; die Seitenzahl
     folgt der Kachelzahl, nicht der Scrollbreite. Die letzte Seite liegt am
     rechten Rand an, auch wenn sie sich mit der vorigen ueberlappt. Hinter der
     letzten Seite kommt wieder die erste, vor der ersten die letzte -- die Reihe
     hat kein Ende, an dem man haengen bleibt. Unter 1000px blendet das CSS den
     Pager aus und die Liste scrollt frei, die Komponente rechnet dann nur mit. */
  class ShopRailPager extends HTMLElement {
    connectedCallback() {
      this.list = document.getElementById(this.getAttribute('for'));
      if (!this.list) return;
      // Der Editor ersetzt Sections im laufenden Betrieb; ohne Abmelden bliebe pro Rendern ein resize-Listener am window.
      this.abortController?.abort();
      this.abortController = new AbortController();
      const { signal } = this.abortController;
      this.prev = this.querySelector('[data-dir="-1"]');
      this.next = this.querySelector('[data-dir="1"]');
      this.count = this.querySelector('.shop-rail__pager-count');
      this.prev.addEventListener('click', () => this.go(-1), { signal });
      this.next.addEventListener('click', () => this.go(1), { signal });
      this.list.addEventListener('scroll', () => this.schedule(), { passive: true, signal });
      window.addEventListener('resize', () => this.schedule(), { passive: true, signal });
      // In einem versteckten Reiter misst die Liste 0px; erst beim Aufdecken stimmt die Seitenzahl.
      this.resizeObserver?.disconnect();
      if (typeof ResizeObserver === 'function') {
        this.resizeObserver = new ResizeObserver(() => this.schedule());
        this.resizeObserver.observe(this.list);
      }
      this.update();
    }

    disconnectedCallback() {
      this.abortController?.abort();
      this.resizeObserver?.disconnect();
      if (this.raf) cancelAnimationFrame(this.raf);
      this.raf = null;
    }

    get cards() {
      return [...this.list.children];
    }

    // Abstand von Kachelanfang zu Kachelanfang, inklusive Luecke.
    get step() {
      const c = this.cards;
      if (c.length < 2) return c[0]?.offsetWidth || 1;
      return c[1].offsetLeft - c[0].offsetLeft || 1;
    }

    get perView() {
      return Math.max(1, Math.round(this.list.clientWidth / this.step));
    }

    get pages() {
      return Math.max(1, Math.ceil(this.cards.length / this.perView));
    }

    get maxScroll() {
      return Math.max(0, this.list.scrollWidth - this.list.clientWidth);
    }

    get page() {
      if (this.list.scrollLeft >= this.maxScroll - 2) return this.pages - 1;
      return Math.min(this.pages - 1, Math.round(this.list.scrollLeft / (this.step * this.perView)));
    }

    go(dir) {
      const pages = this.pages;
      const target = (this.page + dir + pages) % pages;
      const left = target === pages - 1 ? this.maxScroll : target * this.perView * this.step;
      this.list.scrollTo({ left, behavior: 'smooth' });
    }

    schedule() {
      if (this.raf) return;
      this.raf = requestAnimationFrame(() => { this.raf = null; this.update(); });
    }

    update() {
      const pages = this.pages;
      this.hidden = pages < 2;
      this.count.textContent = `${this.page + 1}/${pages}`;
    }
  }

  if (!customElements.get('dreissigacker-tabs')) customElements.define('dreissigacker-tabs', ShopTabs);
  if (!customElements.get('dreissigacker-rail-pager')) customElements.define('dreissigacker-rail-pager', ShopRailPager);
  if (!customElements.get('dreissigacker-cart-sync')) customElements.define('dreissigacker-cart-sync', ShopCartSync);
})();
